// src/components/digital_twin/TwinSparkline.js
// --- UPDATE (Digital Twin MOW+ v1) — NEW FILE -----------------------------------------------
// Small zero-dependency SVG trend line for twin telemetry. Accepts any history shape
// ({psi}, {level}, {value}, {t,v}, numbers; any order) and always draws oldest → newest.
// Optional min / max threshold lines and an ML "normal band" (mean ± 2σ).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useMemo } from 'react';
import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import { seriesOf, num } from './twinIntel';

let uid = 0;
const TwinSparkline = memo(({ data = [], color = '#22d3ee', height = 56, minThreshold = null, maxThreshold = null, band = null, emptyText = 'Awaiting telemetry…', ariaLabel = 'Telemetry trend' }) => {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const emptyBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const emptyBd = useColorModeValue('gray.200', 'whiteAlpha.200');
    const gid = useMemo(() => { uid += 1; return `tws${uid}`; }, []);
    const pts = useMemo(() => seriesOf(data), [data]);

    if (pts.length < 2) {
        return (
            <Flex h={`${height}px`} align="center" justify="center" bg={emptyBg} borderRadius="md" borderWidth="1px" borderStyle="dashed" borderColor={emptyBd}>
                <Text fontSize="xs" color={muted}>{emptyText}</Text>
            </Flex>
        );
    }
    const W = 300, H = height, P = 6;
    const vals = pts.map((p) => p.v);
    const lo = Math.min(...vals, ...(num(minThreshold) !== null ? [num(minThreshold)] : []), ...(band ? [band.lo] : []));
    const hi = Math.max(...vals, ...(num(maxThreshold) !== null ? [num(maxThreshold)] : []), ...(band ? [band.hi] : []));
    const rng = (hi - lo) || 1;
    const y = (v) => P + (H - 2 * P) * (1 - (v - lo) / rng);
    const x = (i) => P + (W - 2 * P) * (i / (pts.length - 1));
    const line = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
    const last = pts[pts.length - 1];
    return (
        <Box h={`${height}px`} w="100%" role="img" aria-label={`${ariaLabel}: ${pts.length} points, latest ${Math.round(last.v * 100) / 100}`}>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.3" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
                {band && <rect x={P} y={y(band.hi)} width={W - 2 * P} height={Math.max(0, y(band.lo) - y(band.hi))} fill={color} opacity="0.08" />}
                {num(maxThreshold) !== null && <line x1={P} x2={W - P} y1={y(num(maxThreshold))} y2={y(num(maxThreshold))} stroke="#ef4444" strokeDasharray="3,3" strokeWidth="1" opacity="0.7" />}
                {num(minThreshold) !== null && <line x1={P} x2={W - P} y1={y(num(minThreshold))} y2={y(num(minThreshold))} stroke="#ef4444" strokeDasharray="3,3" strokeWidth="1" opacity="0.7" />}
                <polygon points={`${line} ${x(pts.length - 1)},${H - P} ${x(0)},${H - P}`} fill={`url(#${gid})`} />
                <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                <circle cx={x(pts.length - 1)} cy={y(last.v)} r="3.5" fill={color} />
            </svg>
        </Box>
    );
});
TwinSparkline.displayName = 'TwinSparkline';
export default TwinSparkline;
